/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useMemo, useState } from "react";
import { SETable } from "../../../../../../../../../../components/SETable";
import { Grid } from "@mui/material";
import { TabButton, TabsWrapper } from "../styles";
import { SEText } from "../../../../../../../../../../components/SEText";
import { AtendimentosSSEService } from "../../../../../../../services";
import useAbortableRequest from "../../../../../../../../../../../hooks/useAbortableRequest";
import {
  removeEmpty,
  removeEmptyProperties,
} from "../../../../../../../../../../utils/utils";
import { useQuery } from "react-query";
import moment from "moment";
import SEInputSearch from "../../../../../../../../../../components/SEInputSearch";
import { SEButtonExport } from "../../../../../../../../../../components/SEButtonExport";
import { tableColumnsConsolidado } from "./columns";
import { FiltersConsolidado } from "../../../../FiltersConsolidado";
import { useReportsFilter } from "../../../../../../../../../../../context/ReportsFilterContext";
import useExcelExport from "../../../../../../../../../../hooks/useExcelExport";

interface Props {
  tabSelected: string;
  setTabSelected: React.Dispatch<React.SetStateAction<string>>;
  tabs: string[];
}

const ConsolidadoTable = ({ tabSelected, setTabSelected, tabs }: Props) => {
  const [searchText, setSearchText] = useState<string | null>(null);
  const { createAbortController } = useAbortableRequest();

  const {
    paginationFilters,
    bodyFilters,
    handleSearchChange,
    handleChangeBodyFilter,
    handlePageChange,
    handleChangeOrder,
    handleLinesPerPage,
    setPaginationFilters,
  } = useReportsFilter();

  const bodyParams = removeEmptyProperties({
    ...bodyFilters,
    oqueVoceProcura: searchText,
    tipoAtendimento: "AGENTE_SSE_CREDITO",
  });

  const { data, isLoading: isLoadingConsolidado } = useQuery({
    queryFn: () =>
      AtendimentosSSEService.findAllConsolidado(
        paginationFilters,
        bodyParams,
        createAbortController()
      ),
    enabled: tabSelected === "Consolidado",
    queryKey: ["abaConsolidadoData", bodyParams, paginationFilters],
  });

  const filename = `Exportacao_Consolidado_CREDITO_${moment().format(
    "DD-MM-YYYY"
  )}`;

  const getDataExcelConsolidado = async () => {
    const bodyParams = removeEmpty({
      ...bodyFilters,
      oqueVoceProcura: searchText,
      tipoAtendimento: "AGENTE_SSE_CREDITO",
    });
    const pageFilter = { ...paginationFilters, page: 0, linesPerPage: 1000 };
    const response = await AtendimentosSSEService.findAllConsolidado(
      pageFilter,
      bodyParams
    );

    return response?.content?.map((xls: any) => ({
      nome: xls?.nome,
      quantidade: xls?.quantidade,
      mei: xls?.mei,
      me: xls?.me,
      epp: xls?.epp,
      na: xls?.na,
      porcentagemMei: xls?.porcentagemMei,
      semFotos: xls?.semFotos,
      semAnotacoes: xls?.semAnotacoes,
      semOrientacoes: xls?.semOrientacoes,
      semAnexos: xls?.semAnexos,
      semAssinaturas: xls?.semAssinaturas,
    }));
  };

  const { executeDownload, downloading } = useExcelExport({
    handleDataCallback: getDataExcelConsolidado,
    filename: filename,
    colorLoading: "#3498db",
    isCsv: true,
  });

  const {
    data: filterOptions,
    isLoading,
    isFetching,
  } = useQuery({
    queryFn: () => {
      const abortController = new AbortController() as any;

      return AtendimentosSSEService.getFilterOptionsConsolidado(
        abortController?.signal
      );
    },
    queryKey: ["abaPorAgenteConsolidadoOptionsData"],
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
  });

  const handleRedirectWithFilter = useCallback(
    (data: any, filter: any, value: any) => {
      const { cpf } = data;
      const atendente = filterOptions?.atendentes?.find(
        (i: any) => i.cpf === cpf
      );
      if (atendente) {
        handleChangeBodyFilter(filter, value);
        setPaginationFilters({
          page: 0,
          linesPerPage: "10",
          orderBy: "id",
          direction: "DESC",
        });
        setTabSelected("Gerencial");
      }
    },
    [filterOptions]
  );

  const memoizedColumns = useMemo(
    () => tableColumnsConsolidado(handleRedirectWithFilter),
    [filterOptions]
  );

  return (
    <>
      <FiltersConsolidado
        {...{ filterOptions, isLoading, isFetching }}
        onClearFilters={() => setSearchText(null)}
      />
      <SETable
        columns={memoizedColumns}
        data={data?.content}
        isLoading={isLoadingConsolidado}
        page={paginationFilters.page}
        direction={paginationFilters?.direction === "ASC" ? "DESC" : "ASC"}
        orderBy={paginationFilters?.orderBy}
        totalPages={data?.totalPages}
        linesPerPageFilters={paginationFilters.linesPerPage}
        onOrderChange={handleChangeOrder}
        onPagePreviousChange={handlePageChange}
        onPageNextChange={handlePageChange}
        onLinesPerPageChange={handleLinesPerPage}
        tableHeader={
          <Grid container paddingBottom={2} gap={2}>
            <Grid item container md={12} justifyContent={"space-between"}>
              <Grid item>
                <TabsWrapper>
                  {tabs.map((tab, index) => (
                    <TabButton
                      key={tab}
                      isActive={tabSelected === tab}
                      index={index}
                      onClick={() => setTabSelected(tab)}
                    >
                      <SEText>{tab}</SEText>
                    </TabButton>
                  ))}
                </TabsWrapper>
              </Grid>
            </Grid>
            <Grid
              justifyContent={"space-between"}
              alignItems={"center"}
              direction={"row"}
              container
            >
              <Grid item>
                <SEInputSearch
                  placeholder="Digite o que você procura"
                  value={searchText ?? ""}
                  onClick={() => handleSearchChange(searchText)}
                  onChange={(e) => setSearchText(e?.target?.value)}
                />
              </Grid>

              <Grid
                item
                display={"flex"}
                direction={"row"}
                alignItems={"center"}
              >
                <Grid item mr={2}>
                  {!!data?.totalElements && (
                    <SEText as="p" font="Mulish">
                      Mostrando <strong>{data?.totalElements}</strong>{" "}
                      {data?.totalElements === 1 ? "resultado" : "resultados"}
                    </SEText>
                  )}
                </Grid>

                <Grid item>
                  <SEButtonExport
                    isLoading={downloading}
                    onClick={executeDownload}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        }
      />
    </>
  );
};

export default ConsolidadoTable;
